/**
 * One idempotency key per attempt, not per call.
 *
 * Keys were minted inside `mutationFn`, so every call got a fresh one — safe
 * for a transparent retry of one call, and useless against the thing that
 * actually happens: a person tapping twice, or tapping again after a spinner
 * that timed out on a request which had in fact gone through. Two taps, two
 * keys, two writes; a second portion on the bill and on the kitchen's rail.
 *
 * An attempt is one request, identified by what it asks for. The same request
 * asked again before one has succeeded is the same attempt and carries the
 * same key, so the server hands back what the first one made. Once it has
 * succeeded, the next identical request is a new attempt: a second burger
 * rung in after the first one landed is a second burger.
 *
 * The one thing this gives up is a deliberate second tap on the same button
 * *while the first is still in flight*, which it treats as a double tap. That
 * is the right way round: the quantity is where "two" is said on purpose, and
 * a duplicate portion is what the kitchen cooks.
 *
 * `usePaymentAttempt` is the same idea with the attempt named by a screen
 * rather than by its payload, because a payment's amount can be retyped
 * between tries and must still be the same attempt.
 */

import { useRef } from 'react';

export class AttemptKeys {
  private current: { key: string; fingerprint: string } | null = null;

  constructor(private readonly mint: () => string = () => crypto.randomUUID()) {}

  /** The key for this request: the open attempt's if it asks the same thing. */
  keyFor(request: unknown): string {
    const fingerprint = stableJson(request);
    if (this.current?.fingerprint === fingerprint) return this.current.key;
    this.current = { key: this.mint(), fingerprint };
    return this.current.key;
  }

  /** The request went through; the next one like it is a new attempt. */
  succeeded(request: unknown): void {
    if (this.current?.fingerprint === stableJson(request)) this.current = null;
  }
}

/** One `AttemptKeys` for the life of the component that owns the mutation. */
export function useAttemptKeys(): AttemptKeys {
  const keys = useRef<AttemptKeys | null>(null);
  if (keys.current === null) keys.current = new AttemptKeys();
  return keys.current;
}

/**
 * JSON with object keys sorted, so `{a, b}` and `{b, a}` are the same request.
 * Arrays keep their order: modifiers picked in a different order are the same
 * dish, but a caller that means that should sort them, and guessing here would
 * merge two requests that differ.
 */
export function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, item: unknown) => {
    if (item === null || typeof item !== 'object' || Array.isArray(item)) return item;
    return Object.fromEntries(
      Object.entries(item as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
    );
  });
}
